'use client';

import { useEffect } from 'react';

const CHUNK_LOAD_ERROR = /(?:chunkloaderror|failed to load chunk|loading (?:chunk|module) .* failed|\/_next\/static\/chunks\/[^\s"']+\.js)/i;
const RECOVERY_KEY = 'mc-chunk-recovery-attempt';
const RECOVERY_WINDOW_MS = 60_000;

function errorText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (value && typeof value === 'object' && 'message' in value) {
    return String((value as { message?: unknown }).message ?? '');
  }
  return '';
}

export function isChunkLoadError(value: unknown): boolean {
  return CHUNK_LOAD_ERROR.test(errorText(value));
}

function clearCachedAppShell() {
  if (typeof caches === 'undefined') return;
  void caches.keys().then((keys) => Promise.all(
    keys
      .filter((key) => key.startsWith('mc-') && !key.startsWith('mc-crew-data-'))
      .map((key) => caches.delete(key)),
  )).catch(() => {
    // Cache cleanup is best effort; the hard navigation still refreshes the app.
  });
}

function recoverFromChunkLoadError() {
  try {
    const lastAttempt = Number(sessionStorage.getItem(RECOVERY_KEY) || 0);
    if (lastAttempt && Date.now() - lastAttempt < RECOVERY_WINDOW_MS) return;
    sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));
  } catch {
    // Private browsing can deny sessionStorage. Continue with the reload.
  }

  clearCachedAppShell();
  window.location.reload();
}

export function ChunkLoadRecovery() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (isChunkLoadError(event)) recoverFromChunkLoadError();
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) recoverFromChunkLoadError();
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}

export function ServiceWorkerRegistrar() {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {
        // SW registration failed — app still works fine without it
      });
    }
  }, []);

  return null;
}
